import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {DeepResearchMessageType} from 'llm/deep-research/types';
import {
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import {GRAY_DARKER, BLUE_DARKER, BLUE_LIGHTEST, GRAY_DARK, PURPLE_LIGHTEST} from 'ui-library/Colors';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export enum DeepResearchTools {
  // Supervisor-level tools
  ResearchSubAgent = 'ResearchSubAgent',
  Think = 'Think',
  CompleteAnswer = 'CompleteAnswer',

  // Sub-agent tools
  SearchTool = 'SearchTool',
  QueryWarehouseTool = 'QueryWarehouseTool',
  DomainGraphSchemaTool = 'DomainGraphSchemaTool',
  DomainGraphQueryTool = 'DomainGraphQueryTool',
  NotebookQueryTool = 'NotebookTool',
}

export const toolDescriptions = {
  [DeepResearchTools.SearchTool]: 'Searching benchling',
  [DeepResearchTools.QueryWarehouseTool]: 'Querying warehouse',
  [DeepResearchTools.DomainGraphSchemaTool]: 'Understanding data models',
  [DeepResearchTools.DomainGraphQueryTool]: 'Querying data models',
  [DeepResearchTools.NotebookQueryTool]: 'Reading notebook',
  [DeepResearchTools.ResearchSubAgent]: 'Researching',
  [DeepResearchTools.Think]: 'Thinking',
  [DeepResearchTools.CompleteAnswer]: 'Completing answer',
};

export const toolIcons: Record<DeepResearchTools, BenchlingIconName> = {
  [DeepResearchTools.SearchTool]: 'magnifying-glass',
  [DeepResearchTools.QueryWarehouseTool]: 'warehouse-full',
  [DeepResearchTools.DomainGraphSchemaTool]: 'lightbulb',
  [DeepResearchTools.DomainGraphQueryTool]: 'lightbulb',
  [DeepResearchTools.NotebookQueryTool]: 'file',
  [DeepResearchTools.ResearchSubAgent]: 'magic-wand',
  [DeepResearchTools.Think]: 'lightbulb',
  [DeepResearchTools.CompleteAnswer]: 'file',
};

export interface ProgressToolCall {
  tool: DeepResearchTools;
  description: string;
  createdAt: Date;
}

export interface ProgressData {
  numSources: number;
  historyThread: Array<ProgressToolCall>;
}

export interface ProgressDeepResearchMessage {
  type: DeepResearchMessageType.PROGRESS;
  progressData: ProgressData;
}
export function ProgressMessage({
  message,
  isSubmitting,
}: {
  message: ProgressDeepResearchMessage;
  isSubmitting: boolean;
}): JSX.Element | null {
  const {progressData} = message;

  if (!progressData) {
    return null;
  }

  return (
    <div className={css(styles.progressContainer)} data-testid="progress-message">
      {isSubmitting ? (
        <>
          <ProgressHeader progressData={progressData} />
          <ProgressActivity progressData={progressData} />
        </>
      ) : (
        <ProgressCompleted progressData={progressData} />
      )}
    </div>
  );
}

export interface ProgressDataProps {
  progressData: ProgressData;
}

export function ProgressCompleted({progressData}: ProgressDataProps): JSX.Element {
  const {numSources} = progressData;
  return (
    <div className={css(styles.progressHeader)}>
      <div className={css(styles.completedSection)}>
        <span className={css(styles.completedText)}>
          Research completed
          {numSources > 0 ? ` • ${numSources} ${pluralize(numSources, 'source', 'sources')}` : ''}
        </span>
        {/* TODO (BNCH-126794): Add expandable progress */}
        {/* <BenchlingIcon icon="chevron-right" /> */}
      </div>
    </div>
  );
}

export function ProgressHeader({progressData}: ProgressDataProps): JSX.Element {
  const {numSources} = progressData;
  return (
    <div className={css(styles.progressHeader)}>
      <div className={css(styles.thinkingSection)}>
        <BenchlingDuotoneIcon
          icon="spinner-third"
          primaryColor={BLUE_DARKER}
          secondaryColor={BLUE_LIGHTEST}
          spin={true}
        />
        <span className={css(styles.thinkingText)}>Thinking...</span>
      </div>
      {numSources > 0 && (
        <div className={css(styles.sourcesText)}>
          {numSources} {pluralize(numSources, 'source', 'sources')}
        </div>
      )}
    </div>
  );
}

export function ProgressActivity({progressData}: ProgressDataProps): JSX.Element {
  return (
    <div className={css(styles.progressContent)}>
      {progressData.historyThread.map((toolCall, index) => (
        <div key={index} className={css(styles.activityItem)}>
          <BenchlingIcon icon={toolIcons[toolCall.tool]} className={css(styles.activityIcon)} />
          <div className={css(styles.activityDescription)}>
            <span className={css(styles.toolDescription)}>{toolDescriptions[toolCall.tool]}</span>
            {/* TODO (BNCH-126793): Add tool summarized tool calls to frontend */}
            {/* {toolCall.description} */}
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  // Progress container
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '8px',
    backgroundColor: PURPLE_LIGHTEST,
  },

  // Progress Header
  progressHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '8px',
  },

  thinkingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
  },

  thinkingText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  sourcesText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARK,
  },

  // Completed
  completedSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },

  completedText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  // Progress Activity
  progressContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: GRAY_DARK,
  },

  activityIcon: {
    width: '16px',
    textAlign: 'center',
  },

  toolDescription: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },

  activityDescription: {
    ...FONT_PROXIMA_14,
    fontSize: '13px',
    color: GRAY_DARKER,
    flex: 1,
  },
});
